import React, { createContext, useCallback, useState } from "react"
import { Table, createDataSource, useDatasource } from "components/Bable"
import {
  createCurrencyExchange,
  deleteCurrencyExchange,
  fetchCurrencyExchanges,
  updateCurrencyExchange,
} from "api/exchangeRates"
import { queryCache, useMutation, useQuery } from "react-query"

import AddNewExchangeRow from "../components/AddNewExchangeRow"
import { Box } from "@material-ui/core"
import Button from "ui/buttons/Button"
import { Container } from "components/Page"
import { EmptyState } from "components/EmptyState"
import { ExchangeRatePayload } from "models/exchangeRates"
import { columns } from "./columns"
import { css } from "@emotion/css"
import { currency_options } from "utils/currencies"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

type Props = {
  clientId: string
}

export interface ExchangeRateWithId extends ExchangeRatePayload {
  id: string
}

const getRowId = (x: ExchangeRateWithId) =>
  `${x.clientId}-${x.sourceCurrency}-${x.targetCurrency}`

export const globalState = createDataSource<ExchangeRateWithId>({
  getRowId,
})

export const CurrencyContext = createContext<{
  exchangeRates?: ExchangeRatePayload[]
  getFilteredCurrencyOptions: (
    baseCurrency: string,
  ) => { value: string; label: string }[]
  onUpdate: (
    row: ExchangeRateWithId,
    label: string,
    value: string | number,
  ) => Promise<void>
  onDelete: (row: {
    clientId: string
    sourceCurrency: string
    targetCurrency: string
  }) => Promise<void>
}>({
  exchangeRates: [],
  getFilteredCurrencyOptions: () => [],
  onUpdate: async () => {},
  onDelete: async () => {},
})

const ExchangeRatesTable = (props: Props) => {
  const { clientId } = props
  const [showAddNewRow, setShowAddNewRow] = useState(false)
  const { t } = useI18n()
  const sendFeedback = useSendFeedBack()

  const { meta } = useAuthenticated()
  const isAgencyAdmin = meta?.userRole === "AGENCY_ADMINISTRATOR"

  const { data: exchangeRates, isLoading } = useQuery(
    ["currency-exchanges", clientId],
    () => fetchCurrencyExchanges(clientId),
    {
      onSuccess: (data: ExchangeRatePayload[]) => {
        const processedExchangeRates = data.map((rate) => ({
          ...rate,
          id: `${rate.clientId}-${rate.sourceCurrency}-${rate.targetCurrency}`, // Generate unique ID
        }))
        globalState.setData(processedExchangeRates)
      },
    },
  )

  const source = useDatasource({ state: globalState })

  const getFilteredCurrencyOptions = (baseCurrency: string) => {
    return currency_options.filter((option) => option.value !== baseCurrency)
  }

  const [createMutation] = useMutation(createCurrencyExchange, {
    onSuccess: () => {
      queryCache.invalidateQueries(["currency-exchanges", clientId]) // Refetch after creating
      setShowAddNewRow(false)
    },
  })

  const [updateMutation] = useMutation(updateCurrencyExchange, {
    onSuccess: () => {
      queryCache.invalidateQueries(["currency-exchanges", clientId]) // Refetch after updating
      sendFeedback(t("admin.exchange_rate.update_success_message"), "success")
    },
    onError: (error) => {
      sendFeedback(t(error.message), "error")
    },
  })

  const [deleteMutation] = useMutation(deleteCurrencyExchange, {
    onSuccess: () => {
      queryCache.invalidateQueries(["currency-exchanges", clientId]) // Refetch after deleting
    },
  })

  const onAddNewRow = useCallback(
    (newRow: ExchangeRatePayload): Promise<void> => {
      return new Promise((resolve, reject) => {
        createMutation(newRow, {
          onSuccess: () => {
            // Update globalState with the new row
            globalState.addRow({
              ...newRow,
              id: `${newRow.clientId}-${newRow.sourceCurrency}-${newRow.targetCurrency}`,
            })
            resolve()
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    },
    [createMutation],
  )

  const onUpdate = useCallback(
    async (
      row: ExchangeRateWithId,
      label: string,
      value: string | number,
    ): Promise<void> => {
      const { id, ...restRow } = row
      const updatedRow = { ...restRow, [label]: value }

      return new Promise((resolve, reject) => {
        updateMutation(updatedRow, {
          onSuccess: () => {
            // Update the existing row in globalState
            globalState.updateRow({ ...updatedRow, id })
            resolve()
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    },
    [updateMutation],
  )

  const onDelete = useCallback(
    async (row: {
      clientId: string
      sourceCurrency: string
      targetCurrency: string
    }): Promise<void> => {
      return new Promise((resolve, reject) => {
        deleteMutation(row, {
          onSuccess: () => {
            globalState.removeRow(
              `${row.clientId}-${row.sourceCurrency}-${row.targetCurrency}`,
            )
            resolve()
          },
          onError: (error) => {
            reject(error)
          },
        })
      })
    },
    [deleteMutation],
  )

  const handleSort = (
    columnId: string,
    a: ExchangeRatePayload,
    b: ExchangeRatePayload,
  ) => {
    // Sorting logic based on column
    if (columnId === "sourceCurrency") {
      return a.sourceCurrency.localeCompare(b.sourceCurrency)
    } else if (columnId === "targetCurrency") {
      return a.targetCurrency.localeCompare(b.targetCurrency)
    } else if (columnId === "exchangeRate") {
      return a.exchangeRate - b.exchangeRate
    }
    return 0
  }

  const onCloseAddNewRow = useCallback(() => {
    setShowAddNewRow(false)
  }, [])

  const filteredColumns = isAgencyAdmin
    ? columns.filter((col) => col.id !== "actions")
    : columns

  return (
    <CurrencyContext.Provider
      value={{
        exchangeRates,
        getFilteredCurrencyOptions,
        onUpdate,
        onDelete,
      }}
    >
      <Container className={styles.container}>
        <div className={styles.wrappedSurface}>
          {showAddNewRow && (
            <AddNewExchangeRow
              onClose={onCloseAddNewRow}
              onCreate={onAddNewRow}
              clientId={clientId}
            />
          )}
          <Box display="flex" justifyContent="flex-end">
            {!isAgencyAdmin && (
              <Button
                color="primary"
                variant="contained"
                onClick={() => setShowAddNewRow(!showAddNewRow)}
                disabled={showAddNewRow}
              >
                {t("admin.exchange_rate.new_exchange_rate")}
              </Button>
            )}
          </Box>
        </div>
        <Table
          source={source}
          pending={isLoading}
          columns={filteredColumns}
          width="100%"
          height={650}
          onSort={handleSort}
          emptyText={
            <EmptyState
              title={t("admin.exchange_rate.none_exchange_rate")}
              subtitle={
                !isAgencyAdmin
                  ? t("admin.exchange_rate.please_add_exchange_rate")
                  : undefined
              }
            />
          }
          className={styles.table}
        />
      </Container>
    </CurrencyContext.Provider>
  )
}

export default ExchangeRatesTable

const styles = {
  tableToolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;
    padding: 0 0 var(--spacing-xxl-2);

    .left {
      display: flex;
      align-items: center;
      & > :not(:first-child) {
        margin-left: var(--spacing-m);
      }
    }
  `,
  noDataTableToolbar: css`
    padding: 0;
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  container: css`
    padding: 0px;
  `,
  surface: css`
    padding: var(--spacing-xxl-2) 0 0;
  `,
  wrappedSurface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
  btn: css`
    margin: 0px;
  `,
  noDataBtn: css`
    margin: 0px 0px var(--spacing-xxl-2);
  `,
}
